import { apiClient } from "../../../lib";

export interface PreguntaOficial {
  idPreguntaOficial: number;
  contenido: string;
  alternativas: Record<string, string>;
  respuesta: string;
  explicacion: string;
  etapaHistoriaId: number;
  vigente?: boolean;
  tieneImagen?: boolean;
  imagen?: Uint8Array | string; // Puede ser Uint8Array (legacy) o string (URL)
}

export interface MundoHistoria {
  idMundoHistoria: number;
  nombre: string;
  descripcion?: string;
  icono?: string;
}

export interface EtapaHistoria {
  idEtapaHistoria: number;
  nombre: string;
  mundoHistoriaId: number;
}

export interface ModoHistoriaDto {
  idPreguntaOficial: number;
  contenido: string;
  alternativas: Record<string, string>;
  respuesta: string;
  explicacion: string;
  etapaHistoriaId: number;
  mundoHistoriaId: number;
  vigente?: boolean;
  tieneImagen?: boolean;
  imagen?: Uint8Array | string; // Desde el backend - puede ser Uint8Array (legacy) o string (URL)
  datosGrafico?: any; // Datos para gráficos interactivos
}

// Interfaz para datos de entrada (con File para imagen)
export interface ModoHistoriaInputDto {
  contenido: string;
  alternativas: Record<string, string>;
  respuesta: string;
  explicacion: string;
  etapaHistoriaId: number;
  preguntaOrden?: number;
  mundoHistoriaId: number;
  vigente?: boolean;
  revisada?: boolean;
  dificultad?: number;
  imagen?: File; // Para entrada desde el frontend
  datosGrafico?: any; // Datos para gráficos interactivos
}

// Interfaz específica para importación JSON (soporta ambos métodos)
export interface PreguntaImportacionDto {
  contenido: string;
  alternativas: Record<string, string>;
  respuesta: string;
  explicacion: string;
  // Método 1: ID directo de etapa
  etapaHistoriaId?: number;
  // Método 2: Orden del mundo y etapa
  mundoOrden?: number;
  etapaOrden?: number;
  preguntaOrden?: number;
  vigente?: boolean;
  dificultad?: number;
  fuente?: string;
  datosGrafico?: any; // Datos para gráficos interactivos
}

export class ModoHistoriaService {
  // Obtener todas las preguntas oficiales
  static async getPreguntasOficiales(): Promise<ModoHistoriaDto[]> {
    const response = await apiClient.get("/modo-historia/preguntas-oficiales");
    return response.data.data || [];
  }

  // Función para convertir File a base64
  private static async convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // reader.result ya incluye el prefijo "data:image/png;base64,"
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Crear nueva pregunta oficial
  static async createPreguntaOficial(
    data: ModoHistoriaInputDto
  ): Promise<ModoHistoriaDto> {
    const formData = new FormData();

    // Agregar todos los campos de texto
    formData.append("contenido", data.contenido);
    formData.append("respuesta", data.respuesta);
    formData.append("explicacion", data.explicacion);
    formData.append("etapaHistoriaId", data.etapaHistoriaId.toString());
    formData.append("mundoHistoriaId", data.mundoHistoriaId.toString());

    if (data.preguntaOrden !== undefined) {
      formData.append("orden", data.preguntaOrden.toString());
    }

    if (data.vigente !== undefined) {
      formData.append("vigente", data.vigente ? "true" : "false");
    }

    if (data.dificultad !== undefined) {
      formData.append("dificultad", data.dificultad.toString());
    }

    // Agregar alternativas como JSON string
    formData.append("alternativas", JSON.stringify(data.alternativas));

    // Agregar datos gráficos si existen
    if (data.datosGrafico) {
      formData.append("datosGrafico", JSON.stringify(data.datosGrafico));
    }

    // Si hay imagen, agregarla como archivo
    if (data.imagen && data.imagen instanceof File) {
      formData.append("imagen", data.imagen);
    }

    const response = await apiClient.post(
      "/modo-historia/preguntas-oficiales",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.data;
  }

  // Actualizar pregunta oficial
  static async updatePreguntaOficial(
    id: number,
    data: Partial<ModoHistoriaInputDto>
  ): Promise<ModoHistoriaDto> {
    const formData = new FormData();

    // Agregar todos los campos de texto que existan
    if (data.contenido) formData.append("contenido", data.contenido);
    if (data.respuesta) formData.append("respuesta", data.respuesta);
    if (data.explicacion) formData.append("explicacion", data.explicacion);
    if (data.etapaHistoriaId)
      formData.append("etapaHistoriaId", data.etapaHistoriaId.toString());
    if (data.preguntaOrden !== undefined)
      formData.append("orden", data.preguntaOrden.toString());
    if (data.mundoHistoriaId)
      formData.append("mundoHistoriaId", data.mundoHistoriaId.toString());
    if (data.vigente !== undefined)
      formData.append("vigente", data.vigente ? "true" : "false");
    if (data.revisada !== undefined) {
      formData.append("revisada", data.revisada ? "true" : "false");
    }
    if (data.dificultad !== undefined)
      formData.append("dificultad", data.dificultad.toString());
    if (data.alternativas)
      formData.append("alternativas", JSON.stringify(data.alternativas));

    // Agregar datos gráficos si existen
    if (data.datosGrafico) {
      formData.append("datosGrafico", JSON.stringify(data.datosGrafico));
    }

    // Si hay imagen, agregarla como archivo
    if (data.imagen && data.imagen instanceof File) {
      formData.append("imagen", data.imagen);
    }

    const response = await apiClient.put(
      `/modo-historia/preguntas-oficiales/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.data;
  }

  // Eliminar pregunta oficial
  static async deletePreguntaOficial(id: number): Promise<void> {
    await apiClient.delete(`/modo-historia/preguntas-oficiales/${id}`);
  }

  // Obtener mundos para dropdown
  static async getMundos(): Promise<MundoHistoria[]> {
    const response = await apiClient.get("/modo-historia/mundos");
    return response.data.data || [];
  }

  // Obtener etapas para dropdown
  static async getEtapas(): Promise<EtapaHistoria[]> {
    const response = await apiClient.get("/modo-historia/etapas");
    return response.data.data || [];
  }

  // Obtener etapas por mundo
  static async getEtapasByMundo(mundoId: number): Promise<EtapaHistoria[]> {
    const response = await apiClient.get(
      `/modo-historia/mundos/${mundoId}/etapas`
    );
    return response.data.data || [];
  }

  // Importar preguntas oficiales desde JSON
  static async importarPreguntasDesdeJSON(data: {
    preguntas: PreguntaImportacionDto[];
  }): Promise<any> {
    const response = await apiClient.post(
      "/modo-historia/preguntas-oficiales/import",
      data
    );
    return response.data;
  }
}

export const modoHistoriaService = new ModoHistoriaService();
